import jsPDF from "jspdf";
import axiosInstance from "../axiosInstance";
import { fronturl, weburl } from "../axiosInstance";
import { getJobPortalAuthConfig } from "./jobPortalAuth";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const BRAND = {
  ink: [15, 23, 42],
  muted: [100, 116, 139],
  line: [226, 232, 240],
  soft: [248, 250, 252],
  blue: [37, 99, 235],
  teal: [20, 184, 166],
  green: [22, 163, 74],
  amber: [245, 158, 11],
  red: [239, 68, 68],
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (value, currency = "INR") => {
  const amount = Number(value || 0);
  const formatted = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount);
  return `${currency} ${formatted}`;
};

const cleanText = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const safeFileName = (value) =>
  String(value || "subscription-invoice")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const resolveAssetUrl = (value) => {
  if (!value) return "";
  const url = String(value).trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || /^data:image\//i.test(url)) return url;
  const normalized = url.replace(/^public[\\/]/, "").replace(/^\/+/, "");
  return `${weburl}${normalized}`;
};

const makeLogoBackgroundTransparent = (dataUrl) =>
  new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const context = canvas.getContext("2d");
      if (!context) {
        resolve({
          dataUrl,
          width: canvas.width,
          height: canvas.height,
          type: dataUrl.includes("image/jpeg") ? "JPEG" : "PNG",
        });
        return;
      }

      context.drawImage(image, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      for (let index = 0; index < pixels.length; index += 4) {
        const red = pixels[index];
        const green = pixels[index + 1];
        const blue = pixels[index + 2];
        const isDarkBackground = red < 28 && green < 28 && blue < 28;

        if (isDarkBackground) {
          pixels[index + 3] = 0;
        }
      }

      context.putImageData(imageData, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: canvas.width,
        height: canvas.height,
        type: "PNG",
      });
    };

    image.onerror = () =>
      resolve({
        dataUrl,
        width: 0,
        height: 0,
        type: dataUrl.includes("image/jpeg") ? "JPEG" : "PNG",
      });

    image.src = dataUrl;
  });

const imageToDataUrl = async (url, options = {}) => {
  if (!url) return "";
  if (url.startsWith("data:image/")) {
    return options.transparentDarkBackground
      ? makeLogoBackgroundTransparent(url)
      : {
          dataUrl: url,
          width: 0,
          height: 0,
          type: url.includes("image/jpeg") ? "JPEG" : "PNG",
        };
  }

  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return "";
    const blob = await response.blob();

    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });

    if (!dataUrl) return "";

    return options.transparentDarkBackground
      ? makeLogoBackgroundTransparent(dataUrl)
      : {
          dataUrl,
          width: 0,
          height: 0,
          type: String(blob.type || "").includes("jpeg") ? "JPEG" : "PNG",
        };
  } catch (error) {
    return "";
  }
};

const fitImageSize = (width, height, maxWidth, maxHeight) => {
  if (!width || !height) {
    return { width: maxWidth, height: maxHeight };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: width * ratio,
    height: height * ratio,
  };
};

const setColor = (doc, color, type = "text") => {
  if (type === "draw") doc.setDrawColor(...color);
  else if (type === "fill") doc.setFillColor(...color);
  else doc.setTextColor(...color);
};

const drawRoundedRect = (doc, x, y, width, height, radius, fillColor, strokeColor) => {
  if (fillColor) setColor(doc, fillColor, "fill");
  if (strokeColor) setColor(doc, strokeColor, "draw");
  doc.roundedRect(x, y, width, height, radius, radius, fillColor && strokeColor ? "FD" : fillColor ? "F" : "S");
};

const drawLabelValue = (doc, label, value, x, y, width = 76) => {
  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(label.toUpperCase(), x, y);

  setColor(doc, BRAND.ink);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(String(value || "").length > 18 ? 8.5 : 10);
  const wrapped = doc.splitTextToSize(cleanText(value), width);
  doc.text(wrapped, x, y + 5);
  return y + 5 + wrapped.length * 5;
};

const drawPill = (doc, text, x, y, color, align = "left") => {
  const width = doc.getTextWidth(text) + 10;
  const left = align === "right" ? x - width : x;
  drawRoundedRect(doc, left, y - 5, width, 9, 4.5, color.bg, null);
  setColor(doc, color.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(text.toUpperCase(), left + width / 2, y + 1, { align: "center" });
};

const getStatusColor = (status) => {
  if (status === "paid") return { bg: [220, 252, 231], text: [22, 101, 52] };
  if (status === "pending") return { bg: [254, 243, 199], text: [146, 64, 14] };
  return { bg: [254, 226, 226], text: [185, 28, 28] };
};

const drawHeader = async (doc, invoice, company) => {
  setColor(doc, [255, 255, 255], "fill");
  doc.rect(0, 0, PAGE_WIDTH, 54, "F");
  setColor(doc, BRAND.line, "draw");
  doc.line(MARGIN, 54, PAGE_WIDTH - MARGIN, 54);

  const logoImage = await imageToDataUrl(resolveAssetUrl(company.logo), {
    transparentDarkBackground: true,
  });
  const companyName = company.name || "The Job Ship";

  if (logoImage?.dataUrl) {
    try {
      const size = fitImageSize(logoImage.width, logoImage.height, 36, 18);
      doc.addImage(
        logoImage.dataUrl,
        logoImage.type,
        MARGIN,
        13,
        size.width,
        size.height,
        undefined,
        "FAST"
      );
    } catch (error) {
      setColor(doc, BRAND.ink);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(companyName, MARGIN, 24);
    }
  } else {
    setColor(doc, BRAND.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(companyName, MARGIN, 24);
  }

  setColor(doc, BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("INVOICE", PAGE_WIDTH - MARGIN, 18, { align: "right" });

  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(doc.splitTextToSize(`#${invoice.id}`, 72), PAGE_WIDTH - MARGIN, 29, {
    align: "right",
  });
  drawPill(doc, invoice.status, PAGE_WIDTH - MARGIN, 44, getStatusColor(invoice.status), "right");
};

const drawCompanyAndCustomer = (doc, invoice, company) => {
  drawRoundedRect(doc, MARGIN, 64, 182, 56, 4, [255, 255, 255], BRAND.line);

  setColor(doc, BRAND.blue);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Billed From", 22, 76);
  doc.text("Billed To", 114, 76);

  setColor(doc, BRAND.ink);
  doc.setFontSize(13);
  doc.text(company.name || "The Job Ship", 22, 86);
  doc.text(invoice.customer.name, 114, 86);

  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(doc.splitTextToSize(company.address || "Company address not configured.", 76), 22, 93);
  doc.text(doc.splitTextToSize(invoice.customer.email, 76), 114, 93);
  doc.text(doc.splitTextToSize(invoice.customer.phone, 76), 114, 101);

  const contact = [company.email, company.phone].filter(Boolean).join(" | ");
  if (contact) doc.text(doc.splitTextToSize(contact, 76), 22, 110);
};

const drawSummary = (doc, invoice) => {
  const top = 130;
  const boxWidth = 42.5;
  const items = [
    ["Invoice Date", invoice.purchaseDate],
    ["Gateway", invoice.gateway],
    ["Order ID", invoice.gatewayOrderId],
    ["Payment ID", invoice.gatewayPaymentId],
  ];

  items.forEach(([label, value], index) => {
    const x = MARGIN + index * 46.5;
    drawRoundedRect(doc, x, top, boxWidth, 26, 4, BRAND.soft, BRAND.line);
    drawLabelValue(doc, label, value, x + 5, top + 9, boxWidth - 10);
  });
};

const drawItemTable = (doc, invoice) => {
  const y = 170;
  drawRoundedRect(doc, MARGIN, y, 182, 58, 4, [255, 255, 255], BRAND.line);

  setColor(doc, BRAND.soft, "fill");
  doc.roundedRect(MARGIN, y, 182, 14, 4, 4, "F");
  setColor(doc, BRAND.line, "draw");
  doc.line(MARGIN, y + 14, 196, y + 14);

  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("PLAN", 22, y + 9);
  doc.text("AUDIENCE", 108, y + 9);
  doc.text("PERIOD", 140, y + 9);
  doc.text("AMOUNT", 188, y + 9, { align: "right" });

  setColor(doc, BRAND.ink);
  doc.setFontSize(10);
  doc.text(doc.splitTextToSize(invoice.plan.title, 76), 22, y + 27);
  doc.setFont("helvetica", "normal");
  doc.text(invoice.audience, 108, y + 27);
  doc.text(doc.splitTextToSize(invoice.plan.duration, 30), 140, y + 27);
  doc.setFont("helvetica", "bold");
  doc.text(invoice.amount, 188, y + 27, { align: "right" });

  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`Start: ${invoice.startsAt}`, 22, y + 44);
  doc.text(`Expiry: ${invoice.expiresAt}`, 70, y + 44);

  setColor(doc, BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Total", 140, y + 48);
  doc.setFontSize(14);
  doc.text(invoice.amount, 188, y + 48, { align: "right" });
};

const drawFooter = (doc, company) => {
  setColor(doc, BRAND.line, "draw");
  doc.line(MARGIN, 267, 196, 267);

  setColor(doc, BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Thank you for your subscription.", MARGIN, 276);

  setColor(doc, BRAND.muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const footer = company.footerCredit || "This is a system-generated subscription invoice.";
  doc.text(doc.splitTextToSize(footer, 135), MARGIN, 283);
  doc.text("Generated by The Job Ship", 196, 283, { align: "right" });
  doc.text(`Page 1 of 1`, 196, 290, { align: "right" });
};

const buildCompanyData = (theme = {}, options = {}) => ({
  name: options.platformName || theme.meta_title || "The Job Ship",
  logo: options.logo || theme.meta_logo || "",
  phone: theme.phone || theme.phone2 || "",
  email: theme.email || theme.email2 || "",
  address: theme.address || "",
  website: options.website || fronturl,
  footerCredit: theme.footer_credit || "",
});

const buildInvoiceData = (order = {}, options = {}) => {
  const status = cleanText(order.status, "pending").toLowerCase();

  return {
    id: order.invoiceNo || order.id || "-",
    status,
    purchaseDate: formatDate(order.purchasedAt || order.createdAt),
    customer: {
      name: order.user?.name || options.userName || "-",
      email: order.user?.email || options.userEmail || "-",
      phone: order.user?.phone || options.userPhone || "-",
    },
    audience: order.audience === "recruiter" ? "Recruiter" : "Job Seeker",
    plan: {
      title: order.plan?.title || order.metadata?.planTitle || "-",
      duration: order.plan?.durationLabel || order.metadata?.durationLabel || "Subscription",
    },
    amount: formatAmount(order.amount, order.currency || "INR"),
    gateway: order.provider || "Razorpay",
    gatewayOrderId: order.providerOrderId || "-",
    gatewayPaymentId: order.providerPaymentId || "-",
    startsAt: formatDate(order.startsAt),
    expiresAt: order.isLifetime ? "Forever" : formatDate(order.expiresAt),
  };
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadSubscriptionInvoiceFromApi = async (order = {}, options = {}) => {
  if (!order.id) return false;

  try {
    const authConfig = await getJobPortalAuthConfig();
    const endpoint = options.admin
      ? `/job-portal/admin/subscription-orders/${order.id}/invoice`
      : `/job-portal/subscription/orders/${order.id}/invoice`;
    const response = await axiosInstance.get(endpoint, {
      ...authConfig,
      responseType: "blob",
    });
    const fileId = safeFileName(order.invoiceNo || order.id).slice(-32) || "subscription";
    downloadBlob(response.data, `subscription-invoice-${fileId}.pdf`);
    return true;
  } catch (error) {
    return false;
  }
};

export const downloadSubscriptionInvoice = async (order = {}, options = {}) => {
  const downloadedFromApi = await downloadSubscriptionInvoiceFromApi(order, options);
  if (downloadedFromApi) return;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const company = buildCompanyData(options.company || options.theme || {}, options);
  const invoice = buildInvoiceData(order, options);

  doc.setProperties({
    title: `${company.name} Subscription Invoice`,
    subject: invoice.plan.title,
    author: company.name,
  });

  setColor(doc, [255, 255, 255], "fill");
  doc.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, "F");

  await drawHeader(doc, invoice, company);
  drawCompanyAndCustomer(doc, invoice, company);
  drawSummary(doc, invoice);
  drawItemTable(doc, invoice);
  drawFooter(doc, company);

  const fileId = safeFileName(invoice.id).slice(-32) || "subscription";
  doc.save(`subscription-invoice-${fileId}.pdf`);
};
